/**
 * NOTE (Yuxin, 2026-02-02): useMorphModifier Hook
 *
 * This hook provides the core functionality for LLM-based visual editing:
 * 1. Takes a modifier instruction (natural language or structured)
 * 2. Calls Groq for instant HTML preview
 * 3. Applies the preview to the DOM via postMessage
 * 4. Queues the file edit for OpenCode to persist
 *
 * The "morph" pattern separates instant visual feedback from persistent changes.
 */
import type { StructuredModifier } from "@agent-ui/features/modifiers/utils/buildModifierPrompt";
import type { UniversalModifierValue } from "schemas/generated/fileModifiers";
import type { SemanticElement } from "schemas/generated/semanticElement";

import { useCallback } from "react";

import { useSessionUuid } from "@agent-ui/features/chat/hooks/useSessionUuid";
import { useIframeMessaging } from "@agent-ui/features/modifiers/hooks/useIframeMessaging";
import {
  useModifiersStoreActions,
  useSemanticElement,
} from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import {
  describeModifier,
  describeUniversalModifier,
} from "@agent-ui/features/modifiers/utils/buildModifierPrompt";
import { useMutation } from "@tanstack/react-query";
import { useReploAgent } from "@v2/contexts/ReploAgentContext";
import { useSearchParams } from "react-router-dom";
import { ReploError, ReploValidationError } from "schemas/errors";

abstract class MorphModifierError extends ReploError {}
class InvalidParentSessionUuidError extends MorphModifierError {}

// =============================================================================
// Types
// =============================================================================

interface MorphModifierResult {
  /** Whether a modifier is currently being processed */
  isProcessing: boolean;
  /** Error message if the last operation failed */
  error: string | null;
  /** Apply a modifier instruction to the selected element(s) */
  applyModifier: (instruction: string) => void;
  /** Apply a structured modifier with natural language translation */
  applyStructuredModifier: (modifier: StructuredModifier) => void;
  /** Apply modifiers by translating them to natural language and routing through Groq */
  applyDirectStyle: (modifiers: UniversalModifierValue[]) => void;
  /** Clear the current selection */
  clearSelection: () => void;
  /** Clear the last error (e.g. after user dismisses) */
  clearError: () => void;
  /** Delete the selected element from the DOM and queue source code removal */
  deleteElement: () => void;
}

/**
 * Converts SemanticElement to the format expected by getSemanticPreview
 */
function toPreviewElement(element: SemanticElement) {
  return {
    elementType: element.elementType,
    selector: element.selector,
    text: element.text,
    cssClasses: element.cssClasses,
    outerHtml: element.outerHtml,
    tagName: element.tagName,
    sourceLocation: element.sourceLocation,
    openingTag: element.openingTag,
    hasChildren: element.hasChildren,
  };
}

/**
 * Converts SemanticElement to the format expected by queueSemanticFileEdit
 */
function toFileEditElement(element: SemanticElement) {
  return {
    elementType: element.elementType,
    selector: element.selector,
    text: element.text,
    cssClasses: element.cssClasses,
    sourceLocation: element.sourceLocation,
    name: element.name,
    path: element.path,
    tagName: element.tagName,
    outerHtml: element.outerHtml,
    nearbyText: element.nearbyText,
    nearbyElements: element.nearbyElements,
  };
}

// =============================================================================
// Hook
// =============================================================================

export function useMorphModifier(): MorphModifierResult {
  const semanticElement = useSemanticElement();
  const modifierActions = useModifiersStoreActions();

  // Get the agent for making RPC calls
  const { actions: agentActions } = useReploAgent();
  const { getSemanticPreview, queueSemanticFileEdit } = agentActions;

  // Get iframe messaging for applying previews
  const { sendMessage } = useIframeMessaging();

  // Get current page route and session UUID from URL
  const [searchParams] = useSearchParams();
  const pageRoute = searchParams.get("pageRoute") ?? "/";
  const sessionUuid = useSessionUuid() ?? undefined;

  const fileEditMutation = useMutation({
    mutationFn: (params: {
      elements: SemanticElement[];
      instruction: string;
      parentSessionUuid: string;
    }) => {
      return queueSemanticFileEdit({
        elements: params.elements.map(toFileEditElement),
        instruction: params.instruction,
        pageRoute,
        parentSessionUuid: params.parentSessionUuid,
      });
    },
  });

  const applyModifierMutation = useMutation({
    mutationFn: async (instruction: string) => {
      const trimmed = instruction.trim();
      if (!trimmed) {
        throw new ReploValidationError("Please enter an instruction");
      }

      const elements = semanticElement ? [semanticElement] : [];
      const firstElement = elements[0];

      if (!firstElement) {
        throw new ReploValidationError("No element selected");
      }

      const result = await getSemanticPreview({
        element: toPreviewElement(firstElement),
        instruction: trimmed,
      });

      const selectors = result.affectedSelectors ?? [];
      const newHtml = result.newHtml;
      const containerOpeningTag = result.containerOpeningTag;

      const appliedSelector = selectors[0];
      if (appliedSelector && (newHtml || containerOpeningTag)) {
        sendMessage({
          type: "APPLY_PREVIEW",
          payload: {
            selector: appliedSelector,
            newHtml,
            containerOpeningTag,
          },
        });

        // NOTE (Derek, 2026-02-24): Register the pending preview immediately so it
        // survives iframe refreshes (HMR, sandbox restart) during the session creation
        // window. sessionUuid is optional — the reapply logic handles missing values.
        modifierActions.addPendingPreview({
          selector: appliedSelector,
          newHtml,
          createdAt: Date.now(),
          pageRoute,
          containerOpeningTag,
        });
      }

      if (!sessionUuid) {
        throw new InvalidParentSessionUuidError({
          message:
            "Parent session UUID not provided - this should never happen",
        });
      }

      fileEditMutation.mutate({
        elements,
        instruction: trimmed,
        parentSessionUuid: sessionUuid,
      });
    },
  });

  /**
   * Apply a structured modifier (e.g., from a modifier block)
   */
  const applyStructuredModifier = useCallback(
    (modifier: StructuredModifier) => {
      const instruction = describeModifier(modifier);
      applyModifierMutation.mutate(instruction);
    },
    [applyModifierMutation],
  );

  /**
   * Clear the current selection
   */
  const clearSelection = useCallback(() => {
    modifierActions.clearSemanticSelection();

    sendMessage({
      type: "CLEAR_SELECTION",
      payload: {},
    });
  }, [modifierActions, sendMessage]);

  /**
   * Delete the selected element: instant DOM removal + async source code edit
   */
  const deleteElement = useCallback(async () => {
    const elements = semanticElement ? [semanticElement] : [];

    if (elements.length === 0) {
      return;
    }
    if (!sessionUuid) {
      throw new InvalidParentSessionUuidError({
        message: "Parent session UUID not provided - this should never happen",
      });
    }

    const element = elements[0]!;

    // NOTE (Derek, 2026-02-12): Use the APPLY_PREVIEW container path to hide the
    // element via display:none. This collapses it from layout entirely (no residual
    // spacing from flex gap, margins, etc.) while keeping the element in the DOM so
    // the selector still works for HMR reapplication. The iframe already handles the
    // container path without needing a scaffold rebuild.
    const hideTag = `<${element.tagName} style="display:none">`;
    sendMessage({
      type: "APPLY_PREVIEW",
      payload: {
        selector: element.selector,
        containerOpeningTag: hideTag,
      },
    });

    // Store deletion as pending preview so it survives HMR reloads
    modifierActions.addPendingPreview({
      selector: element.selector,
      newHtml: "",
      containerOpeningTag: hideTag,
      createdAt: Date.now(),
      pageRoute,
      sessionUuid,
    });

    // Clear selection
    modifierActions.clearSemanticSelection();
    sendMessage({ type: "CLEAR_SELECTION", payload: {} });

    // Queue source code deletion (fire-and-forget)
    fileEditMutation.mutate({
      elements,
      instruction:
        "Delete this element entirely. Remove the JSX element and its children from the source code.",
      parentSessionUuid: sessionUuid,
    });
  }, [
    semanticElement,
    sendMessage,
    modifierActions,
    fileEditMutation,
    pageRoute,
    sessionUuid,
  ]);

  /**
   * Apply modifiers by translating them to natural language and routing through Groq.
   */
  const applyDirectStyle = useCallback(
    (modifiers: UniversalModifierValue[]) => {
      if (modifiers.length === 0) {
        return;
      }
      const instructions = modifiers
        .map(describeUniversalModifier)
        .filter((s) => s.length > 0);
      if (instructions.length > 0) {
        applyModifierMutation.mutate(instructions.join(". "));
      }
    },
    [applyModifierMutation],
  );

  return {
    isProcessing: applyModifierMutation.isPending,
    error: applyModifierMutation.error?.message ?? null,
    applyModifier: applyModifierMutation.mutate,
    applyStructuredModifier,
    applyDirectStyle,
    clearSelection,
    clearError: applyModifierMutation.reset,
    deleteElement: () => void deleteElement(),
  };
}
